import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, switchMap, map, timeout } from 'rxjs';
import { setSentryUser } from '../monitoring/sentry-lazy';
import { environment } from '@env/environment';
import { kvkkConsentMissing } from '../utils/kvkk-gate';
import { BROWSER_STORAGE } from '../platform/browser-storage';
import { MEMBERSHIP_BANNER_KEY } from '../../features/membership/membership.utils';

export interface User {
  _id: string;
  username: string;
  email?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'super_admin' | 'gym_admin' | 'trainer' | 'student';
  gymId?: string;
  isActive: boolean;
  fullName: string;
  // Present on the /me response. false for phone-OTP / Google sign-ups
  // that haven't set a password yet — drives the "Set password" UI.
  hasPassword?: boolean;
  // KVKK rızası (kendi yükünde hep var; rızasızsa null). Güncel sürüm
  // /me ve girişte gelir; sürüm eskiyse kapı yeniden rıza ister.
  kvkkConsentAt?: string | null;
  kvkkConsentVersion?: string | null;
  kvkkCurrentVersion?: string | null;
  /// Stüdyodan çıkarılmış, henüz kendi salonu olmayan eğitmen (spec 2026-10-19).
  departedFrom?: { gymId: string; at: string } | null;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
  };
}

/// "Kullanıcı olarak giriş": yalnız erişim jetonu, yenileme jetonu yok.
export interface ImpersonationResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    accessToken: string;
    refreshToken: null;
  };
}

export interface LoginRequest {
  identifier: string;
  password: string;
}

export interface RegisterBillingInfo {
  invoiceTitle: string;
  taxOffice: string;
  taxNumber: string;
  address: string;
}

export interface RegisterRequest {
  username: string;
  email?: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  // Maps to a role server-side: gym_owner → gym_admin (+ new gym & trial),
  // trainer / student start unattached. Defaults to student if omitted.
  accountType?: 'gym_owner' | 'trainer' | 'student';
  gymName?: string; // required when accountType === 'gym_owner'
  // Salon fatura bilgileri — accountType === 'gym_owner' iken dört alan da ZORUNLU
  // (backend `requiredBillingInfoValidators`; vergi no 10 VKN / 11 TCKN hane).
  billingInfo?: RegisterBillingInfo;
  // KVKK açık rızası (msg 525) — kayıt ekranında zorunlu kutu.
  acceptKvkk?: boolean;
}

/// Sunucunun KVKK rızası olmadan hesap açmayı reddettiği mesaj
/// (backend utils/kvkk-consent.ts ile aynı).
export const KVKK_CONSENT_REQUIRED_MESSAGE = 'KVKK açık rıza onayı gereklidir';

export const KVKK_CONSENT_REQUIRED_CODE = 'KVKK_CONSENT_REQUIRED';

export function isKvkkConsentRequiredError(error: unknown): boolean {
  return error === KVKK_CONSENT_REQUIRED_MESSAGE;
}

/// Sosyal/telefon giriş hatasını bileşenin göstereceği metne çevirir. KVKK
/// reddi makine-okur koddan (`errorCode`) tanınır ve kanonik mesaja
/// eşlenir; eski sunucu için metnin kendisi de yeterli (yedek).
function socialAuthErrorMessage(error: any, fallback: string): string {
  if (error?.error?.errorCode === KVKK_CONSENT_REQUIRED_CODE) {
    return KVKK_CONSENT_REQUIRED_MESSAGE;
  }
  return error?.error?.message || fallback;
}

/// İsteğe yalnız tanımlıysa eklenir: alan YOKSA sunucu geçiş döneminde
/// hesabı damgasız açar; false ise yeni hesap açmayı reddeder (mevcut
/// kullanıcının girişi etkilenmez); true ise rıza damgalanır.
function withKvkk<T extends object>(body: T, acceptKvkk?: boolean): T & { acceptKvkk?: boolean } {
  return acceptKvkk === undefined ? body : { ...body, acceptKvkk };
}

/// Üyelik uyarı bandının kapatma kaydı oturuma bağlı: her girişte/çıkışta silinir (bant her girişte yeniden çıkar).
/// sessionStorage yok ya da engelliyse (SSR, gizli pencere) sessizce geçilir.
function clearMembershipBannerDismissal(): void {
  try {
    sessionStorage.removeItem(MEMBERSHIP_BANNER_KEY);
  } catch {
    // Depolama yoksa silinecek kayıt da yoktur.
  }
}

const ME_REFRESH_TIMEOUT_MS = 8000;

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/auth`;
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly USER_KEY = 'user';
  /// Prerender'da localStorage yok; sunucuda bellek içi depo (ziyaretçi).
  private readonly storage = inject(BROWSER_STORAGE);

  // Signals for reactive state
  private currentUserSignal = signal<User | null>(this.getStoredUser());
  private gymIsActiveSignal = signal<boolean | null>(this.getStoredGymIsActive());

  // Computed values
  readonly currentUser = computed(() => this.currentUserSignal());
  readonly isAuthenticated = computed(() => this.currentUserSignal() !== null);
  readonly userRole = computed(() => this.currentUserSignal()?.role);
  readonly userGymId = computed(() => this.currentUserSignal()?.gymId);
  /// Oturumda yenileme jetonu var mı. "Kullanıcı olarak giriş" (taklit)
  /// oturumunda yok; localStorage computed içinde okunamaz (reaktif değil),
  /// o yüzden sinyalde tutulur ve jeton yazan/silen yerlerde güncellenir.
  private readonly hasRefreshTokenSignal = signal<boolean>(!!this.storage.getItem(this.REFRESH_TOKEN_KEY));
  /// KVKK durumu (güncel sürüm) /me ile henüz doğrulanmadı: önbellekteki
  /// kullanıcıda `kvkkCurrentVersion` yok. İlk /me dönene dek (hata olsa da
  /// açılır) arayüz yükleniyor gösterir; kabuk bir an görünüp kapanmasın.
  private readonly kvkkRefreshPendingSignal = signal<boolean>(
    this.getStoredUser() !== null && this.getStoredUser()?.kvkkCurrentVersion === undefined
  );
  readonly kvkkStatusPending = computed(() => this.kvkkRefreshPendingSignal() && this.isAuthenticated());

  /// Giriş yapmış kullanıcının KVKK rızası eksik/eski mi (engelleyici kapı).
  /// Taklit oturumunda (yenileme jetonu yok) kapı açılmaz: rıza kişinin
  /// kendisinden olmalı, backend taklit oturumundan rıza almıyor.
  readonly kvkkConsentRequired = computed(
    () => kvkkConsentMissing(this.currentUserSignal()) && this.hasRefreshTokenSignal()
  );
  /// True when the signed-in user is attached to a gym. Useful for
  /// hiding/disabling actions that require a gym (add trainer/student,
  /// view lessons, café, etc.) for orphan accounts — e.g. a gym admin
  /// whose previous gym was deleted by the super admin.
  readonly userHasGym = computed(() => !!this.currentUserSignal()?.gymId);

  // Gym inactive status - true if gym is inactive (read-only mode)
  readonly isGymInactive = computed(() => {
    const user = this.currentUserSignal();
    const gymIsActive = this.gymIsActiveSignal();
    // Super admin için her zaman false
    if (user?.role === 'super_admin') return false;
    // gymId yoksa veya gym aktifse false
    if (!user?.gymId) return false;
    // gymIsActive null ise henüz bilinmiyor, false döndür
    if (gymIsActive === null) return false;
    return !gymIsActive;
  });

  /// Salt okunur panel: salon pasif ya da hesap bir salona bağlı değil.
  /// Yazma düğmeleri `disabled` + `READ_ONLY_TITLE` (core/utils); listeler görünür kalır.
  readonly isReadOnly = computed(() => this.isGymInactive() || !this.userHasGym());

  // Alias for compatibility
  user = this.currentUser;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/login`, credentials).pipe(
      tap((response) => {
        if (response.success) {
          this.setSession(response.data);
        }
      }),
      catchError((error) => {
        return throwError(() => error.error?.message || 'Giriş başarısız');
      })
    );
  }

  /// Exchange a Google Identity Services credential (ID token) for a
  /// My-PT session. Backend verifies the token, finds-or-creates the
  /// user, and returns the same envelope as /login so we reuse
  /// setSession() verbatim.
  loginWithGoogle(idToken: string, acceptKvkk?: boolean): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/google`, withKvkk({ idToken }, acceptKvkk)).pipe(
      tap((response) => {
        if (response.success) {
          this.setSession(response.data);
        }
      }),
      catchError((error) => {
        return throwError(() => socialAuthErrorMessage(error, 'Google ile giriş başarısız'));
      })
    );
  }

  /// Exchange a Firebase Phone Auth ID token (obtained after the SMS
  /// OTP handshake) for a My-PT session. Backend verifies the token,
  /// finds-or-creates the user by phone, and returns the standard
  /// envelope so we reuse setSession().
  loginWithPhone(idToken: string, acceptKvkk?: boolean): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/firebase-phone`, withKvkk({ idToken }, acceptKvkk)).pipe(
      tap((response) => {
        if (response.success) {
          this.setSession(response.data);
        }
      }),
      catchError((error) => {
        return throwError(() => socialAuthErrorMessage(error, 'Telefon ile giriş başarısız'));
      })
    );
  }

  logout(): void {
    const token = this.getAccessToken();
    if (token) {
      this.http.post(`${this.API_URL}/logout`, {}).subscribe({
        error: () => {}, // Ignore logout errors
      });
    }
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    // "Kullanıcı olarak giriş" oturumunda yenileme jetonu YOK (backend 1
    // saatlik tek jeton veriyor). Bosuna /refresh cagirmadan hata ver:
    // interceptor 401'de bunu görüp oturumu kapatır, giriş ekranına döner.
    if (!refreshToken) {
      return throwError(() => new Error('Yenileme jetonu yok'));
    }
    return this.http.post<AuthResponse>(`${this.API_URL}/refresh`, { refreshToken }).pipe(
      tap((response) => {
        if (response.success) {
          this.setTokens(response.data.accessToken, response.data.refreshToken);
        }
      }),
      catchError((error) => {
        // Don't eagerly clear session here — interceptor's handleUnauthorized
        // is the single authority for logout when refresh truly fails.
        return throwError(() => error);
      })
    );
  }

  getAccessToken(): string | null {
    return this.storage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.storage.getItem(this.REFRESH_TOKEN_KEY);
  }

  hasRole(roles: string[]): boolean {
    const userRole = this.userRole();
    return userRole ? roles.includes(userRole) : false;
  }

  /// Used by orphan gym admins (gymId === null) to spin up a brand-new
  /// gym + 14-day trial in one shot. Backend re-issues tokens with the
  /// new gymId baked into the JWT so we can update the session in
  /// place — caller does not need to log out.
  createMyGym(gymName: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/me/create-gym`, { gymName }).pipe(
      tap((response) => {
        if (response.success) {
          this.setSession(response.data);
        }
      }),
      catchError((error) => {
        return throwError(() => error.error?.message || 'Spor salonu oluşturulamadı');
      })
    );
  }

  private setSession(data: { user: User; accessToken: string; refreshToken: string | null }): void {
    clearMembershipBannerDismissal();
    this.storage.setItem(this.ACCESS_TOKEN_KEY, data.accessToken);
    // Taklit oturumunda yenileme jetonu yok (null). setItem null'u "null"
    // dizesi olarak saklardi; onun yerine önceki oturumun jetonu da siliniyor.
    if (data.refreshToken) {
      this.storage.setItem(this.REFRESH_TOKEN_KEY, data.refreshToken);
    } else {
      this.storage.removeItem(this.REFRESH_TOKEN_KEY);
    }
    this.hasRefreshTokenSignal.set(!!data.refreshToken);
    this.kvkkRefreshPendingSignal.set(false);
    this.storage.setItem(this.USER_KEY, JSON.stringify(data.user));
    this.currentUserSignal.set(data.user);
    // Attribute future Sentry events to this user. Cleared in clearSession.
    setSentryUser({
      id: data.user._id,
      username: data.user.username,
      email: data.user.email,
    });
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    this.storage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    this.storage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    this.hasRefreshTokenSignal.set(true);
  }

  private clearSession(): void {
    clearMembershipBannerDismissal();
    this.storage.removeItem(this.ACCESS_TOKEN_KEY);
    this.storage.removeItem(this.REFRESH_TOKEN_KEY);
    this.storage.removeItem(this.USER_KEY);
    this.storage.removeItem('gymIsActive');
    this.hasRefreshTokenSignal.set(false);
    this.kvkkRefreshPendingSignal.set(false);
    this.currentUserSignal.set(null);
    this.gymIsActiveSignal.set(null);
    // Stop attributing subsequent Sentry events to the logged-out user.
    setSentryUser(null);
  }

  private getStoredUser(): User | null {
    const userJson = this.storage.getItem(this.USER_KEY);
    if (!userJson) return null;
    // Servis her public sayfada kurulur: bozuk kayıt hidrasyonu kırmasın, ziyaretçi say.
    try {
      return JSON.parse(userJson);
    } catch {
      this.storage.removeItem(this.USER_KEY);
      return null;
    }
  }

  private getStoredGymIsActive(): boolean | null {
    const value = this.storage.getItem('gymIsActive');
    if (value === null) return null;
    return value === 'true';
  }

  setGymIsActive(isActive: boolean): void {
    this.gymIsActiveSignal.set(isActive);
    this.storage.setItem('gymIsActive', String(isActive));
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/public-register`, data).pipe(
      tap((response) => {
        if (response.success) {
          this.setSession(response.data);
        }
      }),
      catchError((error) => {
        return throwError(() => error.error?.message || 'Kayıt başarısız');
      })
    );
  }

  forgotPassword(email: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/forgot-password`, { email }).pipe(
      catchError((error) => {
        return throwError(() => error.error?.message || 'Şifre sıfırlama isteği başarısız');
      })
    );
  }

  resetPassword(token: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/reset-password`, { token, newPassword }).pipe(
      catchError((error) => {
        return throwError(() => error.error?.message || 'Şifre sıfırlama başarısız');
      })
    );
  }

  /// Kayıt sonrası gönderilen doğrulama bağlantısının ucu.
  ///
  /// Kimlik istemiyor: kullanıcı maili başka bir cihazda veya tarayıcıda
  /// açabilir, giriş yapmış olması şart değil. Jetonun kendisi kimlik
  /// yerine geçiyor — şifre sıfırlamadaki kalıbın aynısı.
  verifyEmail(token: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/verify-email`, { token }).pipe(
      catchError((error) => {
        return throwError(() => error.error?.message || 'Doğrulama başarısız');
      })
    );
  }

  updatePassword(currentPassword: string | undefined, newPassword: string): Observable<{ success: boolean; message: string }> {
    // Passwordless (phone/Google) accounts set their first password with
    // no currentPassword; omit it so the backend takes the "set" branch.
    const body: { newPassword: string; currentPassword?: string } = { newPassword };
    if (currentPassword) body.currentPassword = currentPassword;
    return this.http.put<{ success: boolean; message: string }>(`${this.API_URL}/password`, body).pipe(
      catchError((error) => {
        return throwError(() => error.error?.message || 'Şifre güncelleme başarısız');
      })
    );
  }

  deleteAccount(password: string): Observable<{ success: boolean; message: string }> {
    return this.http.request<{ success: boolean; message: string }>('delete', `${this.API_URL}/me`, {
      body: { password },
    }).pipe(
      tap(() => {
        this.clearSession();
        this.router.navigate(['/auth/login']);
      }),
      catchError((error) => {
        return throwError(() => error.error?.message || 'Hesap silinemedi');
      })
    );
  }

  updateProfile(data: { firstName?: string; lastName?: string; email?: string; phone?: string; username?: string }): Observable<{ success: boolean; message: string; data: { user: User } }> {
    return this.http.put<{ success: boolean; message: string; data: { user: User } }>(`${this.API_URL}/profile`, data).pipe(
      tap((response) => {
        if (response.success && response.data?.user) {
          this.currentUserSignal.set(response.data.user);
          this.storage.setItem(this.USER_KEY, JSON.stringify(response.data.user));
        }
      }),
      catchError((error) => {
        return throwError(() => error.error?.message || 'Profil güncelleme başarısız');
      })
    );
  }

  refreshUserData(): void {
    // GET /auth/me returns the user object directly as `data` (not
    // wrapped in { user }), unlike /login. Read it directly — the old
    // `data.user` path was always undefined, so this was silently a
    // no-op (which is why hasPassword never reached the profile).
    // Zaman aşımı: takılan istek "Yükleniyor…" durumunu sonsuza kadar tutmasın.
    this.http.get<{ success: boolean; data: User }>(`${this.API_URL}/me`).pipe(timeout(ME_REFRESH_TIMEOUT_MS)).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.currentUserSignal.set(response.data);
          this.storage.setItem(this.USER_KEY, JSON.stringify(response.data));
        }
        this.kvkkRefreshPendingSignal.set(false);
      },
      // /me düşerse kapıyı kilitli bırakma: kabuğa aç (fail-open).
      error: () => this.kvkkRefreshPendingSignal.set(false),
    });
  }

  /// KVKK onayı: POST sonrası /me ile kullanıcı tazelenir (kapı kalkar).
  giveKvkkConsent(): Observable<void> {
    return this.http.post(`${this.API_URL}/kvkk-consent`, { acceptKvkk: true }).pipe(
      switchMap(() => this.http.get<{ success: boolean; data: User }>(`${this.API_URL}/me`)),
      tap((response) => {
        if (response.success && response.data) this.updateUserData(response.data);
      }),
      map(() => undefined),
      catchError((error) => {
        return throwError(() => error.error?.message || 'KVKK onayı kaydedilemedi');
      })
    );
  }

  updateUserData(user: User): void {
    this.currentUserSignal.set(user);
    this.storage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  impersonate(userId: string): Observable<ImpersonationResponse> {
    return this.http.post<ImpersonationResponse>(`${this.API_URL}/impersonate/${userId}`, {}).pipe(
      tap((response) => {
        if (response.success) {
          // Backend yalnız 1 saatlik erişim jetonu veriyor (refreshToken:
          // null); setSession yenileme jetonunu siliyor. Süre dolunca
          // 401 -> yenileme yok -> çıkış -> giriş ekranı.
          this.setSession(response.data);
        }
      }),
      catchError((error) => {
        return throwError(() => error.error?.message || 'Kullanıcı olarak giriş yapılamadı');
      })
    );
  }
}
